import { useCallback, useMemo } from "react";
import * as yup from "yup";
import { useTypedTranslation } from "../../../hooks";
import { FormValues } from "./EditInvestmentStrategyPage";

export const useEditInvestmentStrategyValidationSchema = (
    fundsWithAllocationRecommendations:
        | {
              maxPercentageAllocation: number;
              id: number;
              name: string;
              description: string;
          }[]
        | undefined,
) => {
    const { t } = useTypedTranslation();

    const findRecommendedFund = useCallback(
        (id: number) => {
            return fundsWithAllocationRecommendations?.find((f) => f.id === id);
        },
        [fundsWithAllocationRecommendations],
    );

    const schema: yup.ObjectSchema<FormValues> = useMemo(
        () =>
            yup.object().shape({
                funds: yup
                    .array()
                    .of(
                        yup.object({
                            id: yup.number().required(),
                            allocation: yup
                                .string()
                                .matches(
                                    /^\d+$/,
                                    t(
                                        "investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.decimalError",
                                    ),
                                )
                                .test({
                                    name: "isValidPercentage",
                                    message: t(
                                        "investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.allocationError",
                                    ),
                                    test: (value) => {
                                        const num = Number(value);
                                        return !isNaN(num) && num >= 0 && num <= 100;
                                    },
                                })
                                .test({
                                    name: "info",
                                    message: t(
                                        "investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.overrideInfo",
                                    ),
                                    test: (value, ctx) => {
                                        const recommendedFund = findRecommendedFund(ctx.parent.id);
                                        const allocation = value ?? "0";
                                        const agree = ctx?.from?.[ctx.from.length - 1].value.agree ?? false;
                                        if (
                                            !agree &&
                                            recommendedFund &&
                                            Number(allocation) > Number(recommendedFund.maxPercentageAllocation)
                                        ) {
                                            return false;
                                        }
                                        return true;
                                    },
                                })
                                .required(
                                    t(
                                        "investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.allocationError",
                                    ),
                                ),
                        }),
                    )
                    .required(),
                remaining: yup
                    .number()
                    .min(
                        0,
                        t("investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.remainingError"),
                    )
                    .max(
                        0,
                        t("investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.remainingError"),
                    )
                    .required(
                        t("investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.remainingError"),
                    )
                    .typeError(
                        t("investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.remainingError"),
                    ),
                agree: yup
                    .boolean()
                    .when("funds", ([funds]: FormValues["funds"][], schema) => {
                        if (
                            funds.some((fund) => {
                                const recommendedFund = findRecommendedFund(fund.id);
                                return (
                                    recommendedFund &&
                                    Number(fund.allocation) > Number(recommendedFund.maxPercentageAllocation)
                                );
                            })
                        ) {
                            return schema.oneOf(
                                [true],
                                t(
                                    "investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.overrideError",
                                ),
                            );
                        }
                        return schema;
                    })
                    .defined(),
            }),
        [t, findRecommendedFund],
    );
    return schema;
};
